import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * LRU session cache — stores card/task snapshots for up to MAX_CACHED sessions.
 * Evicts least-recently-used entry when full.
 */
const MAX_CACHED_SESSIONS = 10;

/**
 * Resolve an authoritative creation timestamp (ms) for a WAL event.
 *
 * Priority: `ts` (explicit creation-time set by backend) →
 * `_stream_id` prefix (publish-time) → `timestamp` (ISO) →
 * `createdAt` (legacy) → null.
 *
 * Without this, live cards fall back to Date.now() at SSE-receive time,
 * which drifts relative to the user bubble's Date.now() click time and
 * can flip the sort order (Processing card rendering above the user's
 * prompt).
 */
function resolveEventTsMs(event) {
  if (!event || typeof event !== 'object') return { ms: null, source: 'none' };
  if (typeof event.ts === 'number' && event.ts > 0) return { ms: event.ts, source: 'ts' };
  const sid = event._stream_id;
  if (typeof sid === 'string') {
    const m = sid.match(/^(\d{12,16})-/);
    if (m) {
      const ms = Number(m[1]);
      if (Number.isFinite(ms) && ms > 0) return { ms, source: '_stream_id' };
    }
  }
  if (typeof event.timestamp === 'string') {
    const parsed = Date.parse(event.timestamp);
    if (Number.isFinite(parsed) && parsed > 0) return { ms: parsed, source: 'timestamp' };
  }
  if (typeof event.createdAt === 'number' && event.createdAt > 0) {
    return { ms: event.createdAt, source: 'createdAt' };
  }
  return { ms: null, source: 'none' };
}

class SessionLRUCache {
  constructor(max) {
    this.max = max;
    /** @type {Map<string, {cards: object, orderedCardIds: string[], tasks: object}>} */
    this.map = new Map();
  }

  get(key) {
    if (!this.map.has(key)) return undefined;
    const value = this.map.get(key);
    // Move to end (most recently used)
    this.map.delete(key);
    this.map.set(key, value);
    return value;
  }

  set(key, value) {
    if (this.map.has(key)) {
      this.map.delete(key);
    } else if (this.map.size >= this.max) {
      // Evict oldest (first key)
      const oldest = this.map.keys().next().value;
      this.map.delete(oldest);
    }
    this.map.set(key, value);
  }

  has(key) {
    return this.map.has(key);
  }

  delete(key) {
    this.map.delete(key);
  }
}

/**
 * useSessionStore — Card/task state machine with session isolation,
 * LRU cache, and loadSession() for historical replay via synthetic events.
 *
 * Replaces useNanoClawResults. Handles card operations:
 *   create_card, stream_to_card, update_card, append_to_card,
 *   replace_card, finalize_card, remove_card
 *
 * Task lifecycle: exec_start, exec_progress, exec_result, exec_error
 *
 * Removed from predecessor:
 *   - html_stream (use stream_to_card with slot:"html")
 *   - exec_activity (use exec_progress)
 *   - intention_update as standalone (intentions are a field on exec_start)
 *
 * @returns {{
 *   cards: Object.<string, CardState>,
 *   orderedCardIds: string[],
 *   tasks: Object.<string, TaskState>,
 *   activeSessionId: string|null,
 *   processEvent: function,
 *   loadSession: function,
 *   switchSession: function,
 *   setActiveSessionId: function,
 *   reset: function,
 * }}
 */
export function useSessionStore() {
  // Card states keyed by cardId
  const [cards, _setCards] = useState({});
  // Ordered card IDs for canvas rendering (insertion order)
  const [orderedCardIds, _setOrderedCardIds] = useState([]);
  // Task lifecycle states keyed by taskId
  const [tasks, _setTasks] = useState({});
  // Exposed active session ID (state — triggers re-renders)
  const [activeSessionId, setActiveSessionIdState] = useState(null);

  // Ref mirror for use inside callbacks without stale closures
  const activeSessionIdRef = useRef(null);

  // Ref MIRRORS for cards / orderedCardIds / tasks.
  // switchSession needs a synchronous read of "the current snapshot" so it can
  // cache the outgoing session before switching. Reading via React state would
  // be subject to React 18 automatic batching — the previous setState-as-read
  // trick was unsafe outside React event handlers because the functional
  // updater could run async, leaving the captured local undefined. These refs
  // are updated in lock-step with every setState via the wrapped setters
  // below, giving us a reliable synchronous "current snapshot".
  const cardsRef = useRef({});
  const orderedCardIdsRef = useRef([]);
  const tasksRef = useRef({});

  // Wrapped setters: call React setState AND update the matching ref.
  // All call sites in this file go through these — never call _setCards etc.
  // directly. Accept value-or-functional-updater (matching React's API).
  const setCards = useCallback((updater) => {
    _setCards((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      cardsRef.current = next;
      return next;
    });
  }, []);
  const setOrderedCardIds = useCallback((updater) => {
    _setOrderedCardIds((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      orderedCardIdsRef.current = next;
      return next;
    });
  }, []);
  const setTasks = useCallback((updater) => {
    _setTasks((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      tasksRef.current = next;
      return next;
    });
  }, []);

  // Streaming slot accumulators (avoids re-renders per chunk)
  const slotAccRef = useRef({}); // cardId:slot → accumulated string

  // Timeout refs for orphaned task cleanup (5-minute TTL)
  const taskTimeoutsRef = useRef({});

  // LRU session cache (persists across re-renders, not across unmounts)
  const sessionCacheRef = useRef(new SessionLRUCache(MAX_CACHED_SESSIONS));

  // ──────────────────────────────────────────────────────────
  // Internal helpers
  // ──────────────────────────────────────────────────────────

  /**
   * Set or clear a 5-minute orphan timeout for a task.
   */
  const resetTaskTimeout = useCallback((taskId) => {
    if (taskTimeoutsRef.current[taskId]) {
      clearTimeout(taskTimeoutsRef.current[taskId]);
    }
    taskTimeoutsRef.current[taskId] = setTimeout(() => {
      delete taskTimeoutsRef.current[taskId];
      setTasks((prev) => {
        const existing = prev[taskId];
        if (!existing) return prev;
        const next = { ...prev };
        next[taskId] = { ...existing, status: 'error', error: 'Task timed out after 5 minutes' };
        return next;
      });
    }, 5 * 60 * 1000);
  }, []);

  const clearTaskTimeout = useCallback((taskId) => {
    if (taskTimeoutsRef.current[taskId]) {
      clearTimeout(taskTimeoutsRef.current[taskId]);
      delete taskTimeoutsRef.current[taskId];
    }
  }, []);

  /**
   * Insert a cardId into the ordered list at the correct position.
   */
  const insertCardId = useCallback((cardId, position) => {
    setOrderedCardIds((prev) => {
      if (prev.includes(cardId)) return prev;
      if (position === 'prepend') return [cardId, ...prev];
      if (typeof position === 'string' && position.startsWith('after:')) {
        const afterId = position.slice(6);
        const idx = prev.indexOf(afterId);
        if (idx >= 0) {
          const next = [...prev];
          next.splice(idx + 1, 0, cardId);
          return next;
        }
      }
      // Default: append
      return [...prev, cardId];
    });
  }, []);

  /**
   * Clean up slot accumulators for a given cardId.
   */
  const cleanupSlotAcc = useCallback((cardId) => {
    Object.keys(slotAccRef.current).forEach((key) => {
      if (key.startsWith(`${cardId}:`)) {
        delete slotAccRef.current[key];
      }
    });
  }, []);

  // ──────────────────────────────────────────────────────────
  // processEvent — the core event processor
  // ──────────────────────────────────────────────────────────

  /**
   * Process a single SSE event from the session event stream.
   * Handles card operations (op field) and lifecycle events (type field).
   *
   * Session isolation: events carrying a sessionId that differs from the active
   * session are silently dropped. Exception: memory_update shows globally.
   */
  const processEvent = useCallback(
    (event) => {
      if (!event) return;

      // Card operations have 'op' field; lifecycle events have 'type' field
      const opOrType = event.op || event.type;
      if (!opOrType) return;

      // ── Session isolation gate ──
      // Drop any event whose sessionId does not match the active session.
      // Belt-and-suspenders against the cross-session leak failure mode:
      //   1. The Pydantic schemas in api-server/app/schemas/redis_events.py now
      //      declare `sessionId` as REQUIRED on every CardOp / lifecycle event,
      //      so a well-behaved publisher can never produce one without it.
      //   2. This gate additionally drops events that nonetheless arrive with
      //      a missing or mismatched sessionId — a future publisher that
      //      bypasses nanoclaw/src/channels/stream-publisher.ts (which is the
      //      single place that runtime-injects sessionId) can no longer
      //      contaminate other sessions, even silently.
      // Whitelist: GLOBAL_OPS bypass the gate because they are intentionally
      // session-less and rendered globally (memory_update card).
      const eventSessionId = event.sessionId || null;
      const activeId = activeSessionIdRef.current;
      const GLOBAL_OPS = new Set(['memory_update']);

      if (activeId && !GLOBAL_OPS.has(opOrType) && eventSessionId !== activeId) {
        console.log(
          `[Store:session] dropped event ${opOrType} for ${eventSessionId || '<missing>'} (active: ${activeId})`
        );
        return; // silently drop — belongs to a different session
      }

      console.log(`[Store:session] processEvent ${opOrType} session=${eventSessionId || activeId}`);

      switch (opOrType) {
        // ============================================================
        // Card Operations (Card Template Protocol)
        // ============================================================

        case 'create_card': {
          const { taskId, cardId, template, data = {}, position, sessionId } = event;
          if (!cardId) return;
          console.log(`[Store:session] card create cardId=${cardId}`);

          // Resolve the authoritative create-time via `ts` → `_stream_id`
          // → `timestamp`. Fall back to a numeric chunk embedded in the
          // cardId, then Date.now() — so a historical card on reload does
          // NOT end up displaying the page-open time as its created-at.
          const tsResolution = resolveEventTsMs(event);
          const tsFromCardId = (() => {
            if (typeof cardId !== 'string') return 0;
            const m = cardId.match(/-(\d{12,16})-/) || cardId.match(/-(\d{12,16})$/);
            return m ? Number(m[1]) : 0;
          })();
          let createdAtMs;
          if (tsResolution.ms) createdAtMs = tsResolution.ms;
          else if (tsFromCardId) createdAtMs = tsFromCardId;
          else createdAtMs = Date.now();

          setCards((prev) => {
            const existing = prev[cardId];
            // If thinking card was eagerly created in exec_start, merge.
            // The WAL event's ts is authoritative — always prefer it when
            // resolved (source !== 'now-fallback').
            if (existing && (template === 'thinking' || template === 'thinking-process')) {
              const walTs = tsResolution.ms || null;
              const mergedCreatedAt = walTs || existing.createdAt || createdAtMs;
              return {
                ...prev,
                [cardId]: {
                  ...existing,
                  template,
                  data: { ...existing.data, ...data },
                  createdAt: mergedCreatedAt,
                  updatedAt: Date.now(),
                },
              };
            }
            return {
              ...prev,
              [cardId]: {
                cardId,
                taskId,
                sessionId: sessionId || null,
                template,
                data: { ...data },
                status: 'streaming',
                createdAt: createdAtMs,
                updatedAt: Date.now(),
              },
            };
          });

          insertCardId(cardId, position);

          // Track cardId in task state
          if (taskId) {
            setTasks((prev) => {
              const existing = prev[taskId];
              if (!existing) return prev;
              return {
                ...prev,
                [taskId]: {
                  ...existing,
                  status: 'streaming',
                  cardIds: [...(existing.cardIds || []), cardId],
                  _ts: Date.now(),
                },
              };
            });
          }
          break;
        }

        case 'stream_to_card': {
          const { cardId, slot, chunk } = event;
          if (!cardId || !slot) return;

          // Accumulate in ref to avoid per-chunk re-renders.
          const accKey = `${cardId}:${slot}`;
          slotAccRef.current[accKey] = (slotAccRef.current[accKey] || '') + (chunk || '');
          const accumulated = slotAccRef.current[accKey];

          setCards((prev) => {
            const card = prev[cardId];
            if (!card) {
              // Card doesn't exist — clean up the accumulator to prevent memory leak
              delete slotAccRef.current[accKey];
              return prev;
            }
            return {
              ...prev,
              [cardId]: {
                ...card,
                data: { ...card.data, [slot]: accumulated },
                updatedAt: Date.now(),
              },
            };
          });
          break;
        }

        case 'update_card': {
          const { cardId, updates } = event;
          if (!cardId || !updates) return;

          setCards((prev) => {
            const card = prev[cardId];
            if (!card) return prev;
            return {
              ...prev,
              [cardId]: {
                ...card,
                data: { ...card.data, ...updates },
                updatedAt: Date.now(),
              },
            };
          });
          break;
        }

        case 'append_to_card': {
          const { cardId, slot, items } = event;
          if (!cardId || !slot || !Array.isArray(items)) return;

          setCards((prev) => {
            const card = prev[cardId];
            if (!card) return prev;
            const existing = Array.isArray(card.data[slot]) ? card.data[slot] : [];
            return {
              ...prev,
              [cardId]: {
                ...card,
                data: { ...card.data, [slot]: [...existing, ...items] },
                updatedAt: Date.now(),
              },
            };
          });
          break;
        }

        case 'replace_card': {
          const { cardId, template, data = {} } = event;
          if (!cardId) return;

          cleanupSlotAcc(cardId);

          setCards((prev) => {
            const card = prev[cardId];
            if (!card) return prev;
            return {
              ...prev,
              [cardId]: {
                ...card,
                template,
                data: { ...data },
                updatedAt: Date.now(),
              },
            };
          });
          break;
        }

        case 'finalize_card': {
          const { cardId } = event;
          if (!cardId) return;
          console.log(`[Store:session] card finalize cardId=${cardId}`);

          cleanupSlotAcc(cardId);

          setCards((prev) => {
            const card = prev[cardId];
            if (!card) return prev;
            return {
              ...prev,
              [cardId]: {
                ...card,
                status: 'finalized',
                updatedAt: Date.now(),
              },
            };
          });
          break;
        }

        case 'remove_card': {
          const { cardId } = event;
          if (!cardId) return;
          console.log(`[Store:session] card remove cardId=${cardId}`);

          cleanupSlotAcc(cardId);

          setCards((prev) => {
            const card = prev[cardId];
            if (!card) return prev;
            return {
              ...prev,
              [cardId]: { ...card, status: 'removed', updatedAt: Date.now() },
            };
          });

          setOrderedCardIds((prev) => prev.filter((id) => id !== cardId));
          break;
        }

        // ============================================================
        // Task Lifecycle Events
        // ============================================================

        case 'exec_start': {
          const { taskId, executor, sessionId, intentions } = event;
          if (!taskId) return;
          console.log(`[Store:session] task ${taskId} \u2192 loading`);

          resetTaskTimeout(taskId);

          // Resolve the exec_start ts so the eager thinking card carries
          // the backend's authoritative create-time, not the SSE-receive
          // clock. Otherwise the card's ts floats up to Date.now() at
          // page reload and can sort above the user bubble's click ts.
          const tsResolution = resolveEventTsMs(event);
          const eagerCreatedAt = tsResolution.ms || Date.now();

          // Eagerly create thinking card — guarantees it exists before any append_to_card
          const thinkingCardId = `thinking-${taskId}`;
          setCards((prev) => {
            if (prev[thinkingCardId]) return prev; // already exists (e.g. from create_card)
            return {
              ...prev,
              [thinkingCardId]: {
                cardId: thinkingCardId,
                taskId,
                sessionId: sessionId || null,
                template: 'thinking-process',
                data: {
                  title: 'Thinking...',
                  steps: [],
                  status: 'active',
                  // Extract intentions from exec_start if present
                  ...(Array.isArray(intentions) && intentions.length > 0 ? { intentions } : {}),
                },
                status: 'streaming',
                createdAt: eagerCreatedAt,
                updatedAt: Date.now(),
              },
            };
          });
          insertCardId(thinkingCardId);

          setTasks((prev) => ({
            ...prev,
            [taskId]: {
              taskId,
              sessionId: sessionId || null,
              executor: executor || '',
              status: 'loading',
              progress: null,
              error: null,
              summary: null,
              cardIds: [thinkingCardId],
              ...(Array.isArray(intentions) && intentions.length > 0 ? { intentions } : {}),
              _ts: Date.now(),
            },
          }));
          break;
        }

        case 'exec_progress': {
          const { taskId, step, total, message } = event;
          if (!taskId) return;

          setTasks((prev) => {
            const existing = prev[taskId];
            if (!existing) return prev;
            return {
              ...prev,
              [taskId]: {
                ...existing,
                progress: { step, total, message },
                _ts: Date.now(),
              },
            };
          });
          break;
        }

        case 'exec_result': {
          const { taskId, summary } = event;
          if (!taskId) return;
          console.log(`[Store:session] task ${taskId} \u2192 done`);

          clearTaskTimeout(taskId);

          // Finalize thinking card — mark as done
          const doneCardId = `thinking-${taskId}`;
          setCards((prev) => {
            const card = prev[doneCardId];
            if (!card) return prev;
            return {
              ...prev,
              [doneCardId]: {
                ...card,
                data: { ...card.data, status: 'done' },
                status: 'finalized',
                updatedAt: Date.now(),
              },
            };
          });

          setTasks((prev) => {
            const existing = prev[taskId];
            if (!existing) return prev;
            return {
              ...prev,
              [taskId]: {
                ...existing,
                status: 'done',
                summary: summary || '',
                _ts: Date.now(),
              },
            };
          });
          break;
        }

        case 'exec_error': {
          const { taskId, error, recoverable } = event;
          if (!taskId) return;
          console.log(`[Store:session] task ${taskId} \u2192 error`);

          clearTaskTimeout(taskId);

          // Mark thinking card as error
          const errCardId = `thinking-${taskId}`;
          setCards((prev) => {
            const card = prev[errCardId];
            if (!card) return prev;
            return {
              ...prev,
              [errCardId]: {
                ...card,
                data: { ...card.data, status: 'error' },
                status: 'finalized',
                updatedAt: Date.now(),
              },
            };
          });

          setTasks((prev) => {
            const existing = prev[taskId];
            if (!existing) return prev;
            return {
              ...prev,
              [taskId]: {
                ...existing,
                status: 'error',
                error: error || 'Unknown error',
                recoverable: !!recoverable,
                _ts: Date.now(),
              },
            };
          });
          break;
        }

        case 'exec_stopped': {
          const { taskId: stoppedTaskId } = event;
          if (!stoppedTaskId) return;
          console.log(`[Store:session] task ${stoppedTaskId} \u2192 stopped`);

          clearTaskTimeout(stoppedTaskId);

          // Mark thinking card as stopped (preserves partial steps)
          const stoppedCardId = `thinking-${stoppedTaskId}`;
          setCards((prev) => {
            const card = prev[stoppedCardId];
            if (!card) return prev;
            return {
              ...prev,
              [stoppedCardId]: {
                ...card,
                data: { ...card.data, status: 'stopped' },
                status: 'finalized',
                updatedAt: Date.now(),
              },
            };
          });

          setTasks((prev) => {
            const existing = prev[stoppedTaskId];
            if (!existing) return prev;
            return {
              ...prev,
              [stoppedTaskId]: {
                ...existing,
                status: 'stopped',
                _ts: Date.now(),
              },
            };
          });
          break;
        }

        case 'exec_timeout': {
          const { taskId: timedOutTaskId, elapsed, maxDuration } = event;
          if (!timedOutTaskId) return;
          console.log(`[Store:session] task ${timedOutTaskId} → timeout (${elapsed}ms, max ${maxDuration}ms)`);

          clearTaskTimeout(timedOutTaskId);

          // Mark thinking card as timed-out
          const toCardId = `thinking-${timedOutTaskId}`;
          setCards((prev) => {
            const card = prev[toCardId];
            if (!card) return prev;
            return {
              ...prev,
              [toCardId]: {
                ...card,
                data: { ...card.data, status: 'timeout' },
                status: 'finalized',
                updatedAt: Date.now(),
              },
            };
          });

          setTasks((prev) => {
            const existing = prev[timedOutTaskId];
            if (!existing) return prev;
            return {
              ...prev,
              [timedOutTaskId]: {
                ...existing,
                status: 'timeout',
                timedOut: true,
                elapsed,
                maxDuration,
                _ts: Date.now(),
              },
            };
          });
          break;
        }

        case 'exec_queued': {
          const { taskId: queuedTaskId, position, estimatedWait } = event;
          if (!queuedTaskId) return;
          console.log(`[Store:session] task ${queuedTaskId} queued at position ${position}`);

          setTasks((prev) => {
            const existing = prev[queuedTaskId];
            // Task may not exist yet (exec_queued can arrive before exec_start)
            const base = existing || { taskId: queuedTaskId, status: 'queued', _ts: Date.now() };
            return {
              ...prev,
              [queuedTaskId]: {
                ...base,
                queuePosition: position,
                estimatedWait: estimatedWait || null,
                _ts: Date.now(),
              },
            };
          });
          break;
        }

        case 'exec_heartbeat': {
          const { taskId: hbTaskId, elapsed: hbElapsed } = event;
          if (!hbTaskId) return;
          // Don't log every heartbeat — too noisy

          setTasks((prev) => {
            const existing = prev[hbTaskId];
            if (!existing) return prev;
            return {
              ...prev,
              [hbTaskId]: {
                ...existing,
                lastHeartbeat: Date.now(),
                elapsed: hbElapsed,
                mayBeStuck: false, // heartbeat received — not stuck
                _ts: Date.now(),
              },
            };
          });
          break;
        }

        // ============================================================
        // Global events (bypass session isolation)
        // ============================================================

        case 'memory_update': {
          // memory_update creates a card with no sessionId — visible globally.
          // The event is expected to carry cardId, template, data fields.
          const { cardId, template, data = {} } = event;
          if (!cardId) return;

          setCards((prev) => ({
            ...prev,
            [cardId]: {
              cardId,
              taskId: null,
              sessionId: null, // intentionally global
              template: template || 'memory-update',
              data: { ...data },
              status: 'finalized',
              createdAt: Date.now(),
              updatedAt: Date.now(),
            },
          }));

          insertCardId(cardId);
          break;
        }

        default:
          break;
      }
    },
    [insertCardId, resetTaskTimeout, clearTaskTimeout, cleanupSlotAcc],
  );

  // ──────────────────────────────────────────────────────────
  // loadSession — replay historical session data via synthetic events
  // ──────────────────────────────────────────────────────────

  /**
   * Load a historical session from REST API data.
   * Converts persisted cards and timeline into synthetic events
   * and plays them through processEvent for consistent state.
   *
   * @param {object} sessionData — session object from API with:
   *   - id: string (session ID)
   *   - result?.cards: Record<string, {template, data, ts?}>
   *   - timeline?: Array<{type, content, ts?, requestTs?}>
   */
  const loadSession = useCallback(
    (sessionData) => {
      if (!sessionData?.id) return;

      const sessionId = sessionData.id;

      const cardCount = sessionData.result?.cards ? Object.keys(sessionData.result.cards).length : 0;
      const taskCount = (sessionData.timeline || []).length;
      console.log(`[Store:session] loadSession ${sessionId} (${cardCount} cards, ${taskCount} tasks)`);

      // Check LRU cache first
      const cached = sessionCacheRef.current.get(sessionId);
      if (cached) {
        activeSessionIdRef.current = sessionId;
        setActiveSessionIdState(sessionId);
        setCards(cached.cards);
        setOrderedCardIds(cached.orderedCardIds);
        setTasks(cached.tasks);
        slotAccRef.current = {};
        return;
      }

      // Clear current state for the new session
      Object.values(taskTimeoutsRef.current).forEach(clearTimeout);
      taskTimeoutsRef.current = {};
      slotAccRef.current = {};
      setCards({});
      setOrderedCardIds([]);
      setTasks({});

      activeSessionIdRef.current = sessionId;
      setActiveSessionIdState(sessionId);

      // Build synthetic events from persisted data

      // 1. Reconstruct cards from session result
      const resultCards = sessionData.result?.cards;
      if (resultCards && typeof resultCards === 'object') {
        const cardEntries = Object.entries(resultCards)
          .filter(([, card]) => card?.template)
          .sort((a, b) => (a[1].ts || 0) - (b[1].ts || 0));

        // Filter out auto-generated summary HTML cards when real result cards exist
        const hasRealCards = cardEntries.some(([id]) => !id.startsWith('html-'));
        const filteredEntries = hasRealCards
          ? cardEntries.filter(([id]) => !id.startsWith('html-'))
          : cardEntries;

        for (const [cardId, card] of filteredEntries) {
          // Synthetic create_card
          processEvent({
            op: 'create_card',
            cardId,
            sessionId,
            template: card.template,
            data: card.data || {},
          });

          // Immediately finalize — historical cards are always done
          processEvent({
            op: 'finalize_card',
            cardId,
          });
        }
      }

      // 2. Convert timeline entries into bubble-like cards
      //    Timeline entries are user/agent messages; exec entries are skipped
      //    since their content is already in result cards.
      const timeline = sessionData.timeline || [];
      if (timeline.length > 0) {
        const sorted = [...timeline].sort(
          (a, b) => (a.requestTs || a.ts || 0) - (b.requestTs || b.ts || 0)
        );
        sorted.forEach((entry, i) => {
          // Skip execution responses — duplicate of canvas card content
          if (entry.type === 'gateway' || entry.type === 'exec') return;

          const role = entry.type === 'user' ? 'user' : 'agent';
          const cardId = `timeline-${sessionId}-${i}`;

          processEvent({
            op: 'create_card',
            cardId,
            sessionId,
            template: 'bubble',
            data: {
              role,
              content: entry.content,
              collapsible: true,
            },
          });

          processEvent({
            op: 'finalize_card',
            cardId,
          });
        });
      }

      // Cache the result (state will be set by processEvent calls above,
      // but we need to capture it after React batches the updates).
      // We use a microtask to read state after the batch completes.
      // However, since we can't read state synchronously after setState,
      // we build the cache entry from the synthetic data directly.
      const cachedCards = {};
      const cachedOrderedIds = [];
      const cachedTasks = {};

      // Rebuild from result cards
      if (resultCards && typeof resultCards === 'object') {
        const cardEntries = Object.entries(resultCards)
          .filter(([, card]) => card?.template)
          .sort((a, b) => (a[1].ts || 0) - (b[1].ts || 0));
        const hasRealCards = cardEntries.some(([id]) => !id.startsWith('html-'));
        const filteredEntries = hasRealCards
          ? cardEntries.filter(([id]) => !id.startsWith('html-'))
          : cardEntries;

        for (const [cardId, card] of filteredEntries) {
          cachedCards[cardId] = {
            cardId,
            taskId: undefined,
            sessionId,
            template: card.template,
            data: { ...(card.data || {}) },
            status: 'finalized',
            createdAt: card.ts || Date.now(),
            updatedAt: card.ts || Date.now(),
          };
          cachedOrderedIds.push(cardId);
        }
      }

      // Rebuild from timeline
      if (timeline.length > 0) {
        const sorted = [...timeline].sort(
          (a, b) => (a.requestTs || a.ts || 0) - (b.requestTs || b.ts || 0)
        );
        sorted.forEach((entry, i) => {
          if (entry.type === 'gateway' || entry.type === 'exec') return;
          const role = entry.type === 'user' ? 'user' : 'agent';
          const cardId = `timeline-${sessionId}-${i}`;
          cachedCards[cardId] = {
            cardId,
            taskId: null,
            sessionId,
            template: 'bubble',
            data: { role, content: entry.content, collapsible: true },
            status: 'finalized',
            createdAt: Date.now(),
            updatedAt: Date.now(),
          };
          cachedOrderedIds.push(cardId);
        });
      }

      sessionCacheRef.current.set(sessionId, {
        cards: cachedCards,
        orderedCardIds: cachedOrderedIds,
        tasks: cachedTasks,
      });
    },
    [processEvent],
  );

  // ──────────────────────────────────────────────────────────
  // switchSession — save current to cache, restore target
  // ──────────────────────────────────────────────────────────

  /**
   * Switch to a different session. Saves the current session's state
   * to the LRU cache and restores the target (from cache if available).
   */
  const switchSession = useCallback(
    (newSessionId) => {
      const currentId = activeSessionIdRef.current;
      console.log(`[Store:session] switchSession ${currentId} \u2192 ${newSessionId}`);

      // Save current session to cache (if we have one).
      // Read directly from refs — synchronous, reliable. The previous
      // setState(prev => capture=prev; return prev) trick was unsafe outside
      // React event handlers because React 18 batching can run the functional
      // updater asynchronously, leaving currentCards undefined and silently
      // dropping the cache write. The ref mirrors above are kept in lock-step
      // with state via setCards / setOrderedCardIds / setTasks wrappers.
      if (currentId) {
        const currentCards = cardsRef.current;
        const currentOrderedIds = orderedCardIdsRef.current;
        const currentTasks = tasksRef.current;

        if (currentCards && Object.keys(currentCards).length > 0) {
          sessionCacheRef.current.set(currentId, {
            cards: currentCards,
            orderedCardIds: currentOrderedIds,
            tasks: currentTasks,
          });
        }
      }

      // Clear timeouts and accumulators
      Object.values(taskTimeoutsRef.current).forEach(clearTimeout);
      taskTimeoutsRef.current = {};
      slotAccRef.current = {};

      activeSessionIdRef.current = newSessionId || null;
      setActiveSessionIdState(newSessionId || null);

      if (!newSessionId) {
        // No session — full reset
        setCards({});
        setOrderedCardIds([]);
        setTasks({});
        return;
      }

      // Try to restore from cache
      const cached = sessionCacheRef.current.get(newSessionId);
      if (cached) {
        setCards(cached.cards);
        setOrderedCardIds(cached.orderedCardIds);
        setTasks(cached.tasks);
      } else {
        // No cached data — start clean (caller should use loadSession for historical)
        setCards({});
        setOrderedCardIds([]);
        setTasks({});
      }
    },
    [setCards, setOrderedCardIds, setTasks],
  );

  // ──────────────────────────────────────────────────────────
  // setActiveSessionId — lightweight setter (no cache logic)
  // ──────────────────────────────────────────────────────────

  const setActiveSessionId = useCallback((sessionId) => {
    activeSessionIdRef.current = sessionId || null;
    setActiveSessionIdState(sessionId || null);
  }, []);

  // ──────────────────────────────────────────────────────────
  // reset — full state wipe
  // ──────────────────────────────────────────────────────────

  /**
   * Reset all state — call when starting a fresh session from this client.
   * Optionally accepts the new session ID to immediately scope future events.
   */
  const reset = useCallback((newSessionId) => {
    activeSessionIdRef.current = newSessionId || null;
    setActiveSessionIdState(newSessionId || null);
    Object.values(taskTimeoutsRef.current).forEach(clearTimeout);
    taskTimeoutsRef.current = {};
    slotAccRef.current = {};
    setCards({});
    setOrderedCardIds([]);
    setTasks({});
  }, []);

  // Stale heartbeat detection — check every 15s if any running task hasn't received
  // a heartbeat in 60s. If so, mark it as potentially stuck.
  useEffect(() => {
    const STALE_THRESHOLD_MS = 60_000;
    const CHECK_INTERVAL_MS = 15_000;

    const interval = setInterval(() => {
      setTasks((prev) => {
        const now = Date.now();
        let changed = false;
        const next = { ...prev };

        for (const [taskId, task] of Object.entries(prev)) {
          const isRunning = task.status === 'loading' || task.status === 'streaming';
          if (isRunning && task.lastHeartbeat && !task.mayBeStuck) {
            if (now - task.lastHeartbeat > STALE_THRESHOLD_MS) {
              next[taskId] = { ...task, mayBeStuck: true, _ts: now };
              changed = true;
              console.log(`[Store:session] task ${taskId} may be stuck (no heartbeat for ${STALE_THRESHOLD_MS / 1000}s)`);
            }
          }
        }

        return changed ? next : prev;
      });
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, []);

  // Clean up all timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(taskTimeoutsRef.current).forEach(clearTimeout);
      taskTimeoutsRef.current = {};
    };
  }, []);

  /**
   * Force-mark a task as 'stopped' without waiting for an `exec_stopped`
   * SSE event. Called from the user-clicked Stop button so the UI clears
   * its Processing pill immediately — important when the server-side
   * task has already errored and won't round-trip an exec_stopped event
   * (e.g., FAST-lane abort after provider error).
   */
  const markTaskStopped = useCallback((taskId) => {
    if (!taskId) return;
    setTasks((prev) => {
      const existing = prev[taskId];
      if (!existing) return prev;
      return {
        ...prev,
        [taskId]: { ...existing, status: 'stopped', _ts: Date.now() },
      };
    });
  }, [setTasks]);

  return {
    // State (triggers re-renders)
    cards,
    orderedCardIds,
    tasks,
    activeSessionId,

    // Actions
    processEvent,
    loadSession,
    switchSession,
    setActiveSessionId,
    reset,
    markTaskStopped,
  };
}
